
import Vue from 'vue';
import { mapState } from 'vuex';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { PLUGIN_KIND, PLUGIN_KIND_LABEL, PluginKind } from '@run-diffusion/shared';
import MiroOnboardingSteps from '@/views/PluginOnboarding/steps/MiroOnboardingSteps.vue';
import PhotoshopOnboardingSteps from '@/views/PluginOnboarding/steps/PhotoshopOnboardingSteps.vue';
import RevitOnboardingSteps from '@/views/PluginOnboarding/steps/RevitOnboardingSteps.vue';
import RhinoOnboardingSteps from '@/views/PluginOnboarding/steps/RhinoOnboardingSteps.vue';
import OmniverseOnboardingSteps from '@/views/PluginOnboarding/steps/OmniverseOnboardingSteps.vue';

export default Vue.extend({
	name: 'PluginOnboarding',
	components: {
		MiroOnboardingSteps,
		PhotoshopOnboardingSteps,
		RevitOnboardingSteps,
		RhinoOnboardingSteps,
		OmniverseOnboardingSteps,
	},
	metaInfo: {
		title: 'Get started | RunDiffusion',
	},
	data () {
		return {
			ctx: null as gsap.Context | null,
		};
	},
	computed: {
		...mapState(['authUser']),
		plugin (): string {
			return String(this.$route.params.plugin || '').toLowerCase();
		},
		pluginLabel (): string {
			return PLUGIN_KIND_LABEL[this.plugin as PluginKind] || 'your plugin';
		},
		isMiro (): boolean {
			return this.plugin === PLUGIN_KIND.MIRO;
		},
		isPhotoshop (): boolean {
			return this.plugin === PLUGIN_KIND.PHOTOSHOP;
		},
		isRevit (): boolean {
			return this.plugin === PLUGIN_KIND.REVIT;
		},
		isRhino (): boolean {
			return this.plugin === PLUGIN_KIND.RHINO;
		},
		isOmniverse (): boolean {
			return this.plugin === PLUGIN_KIND.OMNIVERSE;
		},
		hasWalkthrough (): boolean {
			// Plugins with a full step-by-step walkthrough (rail + GSAP engine run).
			return this.isMiro || this.isPhotoshop || this.isRevit || this.isRhino || this.isOmniverse;
		},
		stepCount (): number {
			// Revit folds the render queue into Generate (4 steps); the others run the full 5.
			return this.isRevit ? 4 : 5;
		},
		railNums (): string[] {
			return Array.from({ length: this.stepCount }, (_, i) => String(i + 1).padStart(2, '0'));
		},
		brandMarkId (): string {
			if (this.isMiro) { return '#po-miro-mark'; }
			if (this.isPhotoshop) { return '#po-ps-mark'; }
			if (this.isRevit) { return '#po-revit-mark'; }
			if (this.isRhino) { return '#po-rhino-mark'; }
			// Omniverse has no mark here yet. The lockup already hides the glyph
			// when this is null and reads "RunDiffusion x Omniverse", so the page
			// is correct without one; drop NVIDIA's official vector in as a
			// <symbol id="po-omniverse-mark"> beside the others and return it.
			return null;
		},
		email (): string {
			return (this.authUser && this.authUser.email) || '';
		},
		initials (): string {
			const e = this.email;
			return e ? e.slice(0, 2).toUpperCase() : 'PA';
		},
		heroSub (): string {
			if (this.isMiro) {
				return 'RunDiffusion is now linked to Miro. Here’s the whole flow — from opening the panel to dropping a render on your board.';
			}
			if (this.isPhotoshop) {
				return 'RunDiffusion is now linked to Photoshop. Here’s the whole flow — from opening the panel to dropping a render straight onto your canvas.';
			}
			if (this.isRevit) {
				return 'RunDiffusion is now linked to Revit. Here’s the whole flow — from opening the panel to reviewing your renders in Studio.';
			}
			if (this.isRhino) {
				return 'RunDiffusion is now linked to Rhino. Here’s the whole flow, from opening the panel to turning a viewport into a finished render.';
			}
			if (this.isOmniverse) {
				return 'RunDiffusion is now linked to Omniverse. Here’s the whole flow, from framing a viewport to putting a generated asset on your stage.';
			}
			return `RunDiffusion is now linked to ${this.pluginLabel}.`;
		},
		ctaSub (): string {
			if (this.isMiro) { return 'Head back to your board and drop your first render.'; }
			if (this.isPhotoshop) { return 'Head back to Photoshop and drop your first render.'; }
			if (this.isRevit) { return 'Head back to Revit and render your first view.'; }
			if (this.isRhino) { return 'Head back to Rhino and render your first viewport.'; }
			if (this.isOmniverse) { return 'Head back to Omniverse and render your first viewport.'; }
			return `Head back to ${this.pluginLabel} and start creating.`;
		},
		ctaButton (): string {
			if (this.isMiro) { return 'Back to your board'; }
			if (this.isPhotoshop) { return 'Back to Photoshop'; }
			if (this.isRevit) { return 'Back to Revit'; }
			if (this.isRhino) { return 'Back to Rhino'; }
			if (this.isOmniverse) { return 'Back to Omniverse'; }
			return `Back to ${this.pluginLabel}`;
		},
		tutorialLinks (): Array<{ kind: 'video' | 'article'; url: string; eyebrow: string; title: string; sub: string }> {
			// Set-apart promo cards — a video walkthrough and the written tutorial.
			// Add other plugins' links here as their pages ship.
			if (this.isPhotoshop) {
				return [
					{ kind: 'video', url: 'https://www.youtube.com/watch?v=ZGBeHRdqfjw', eyebrow: 'Go deeper · Video', title: `Watch the ${this.pluginLabel} walkthrough`, sub: `A guided video tour of everything the ${this.pluginLabel} plugin can do.` },
					{ kind: 'article', url: 'https://www.rundiffusion.com/rundiffusion-photoshop-plugin', eyebrow: 'Go deeper · Tutorial', title: `Read the ${this.pluginLabel} tutorial`, sub: 'The full written guide, step by step.' },
				];
			}
			return [];
		},
	},
	mounted () {
		if (!this.hasWalkthrough) {
			return;
		}
		// Defer to next tick so the DOM (and layout used by the drag/fly
		// distance math) is settled before we wire ScrollTrigger.
		this.$nextTick(() => this.initAnimations());
	},
	beforeDestroy () {
		if (this.ctx) {
			this.ctx.revert();
			this.ctx = null;
		}
	},
	methods: {
		onBack () {
			// Best-effort: the page is opened in a fresh tab by the plugin, so
			// closing it returns the user to their board. window.close() only
			// works for script-opened tabs; harmless otherwise.
			window.close();
		},
		initAnimations () {
			try {
				gsap.registerPlugin(ScrollTrigger);
				const root = this.$el as HTMLElement;
				const ticks = Array.from(root.querySelectorAll('.po-rail__tick'));
				const setActive = (i: number) => {
					ticks.forEach((t, n) => t.classList.toggle('is-active', n === i));
				};

				this.ctx = gsap.context(() => {
					const mm = gsap.matchMedia();

					// Full motion.
					mm.add('(prefers-reduced-motion: no-preference)', () => {
						// Background aurora parallax.
						gsap.to('.po-aurora--1', { yPercent: 20, ease: 'none', scrollTrigger: { trigger: root, start: 'top top', end: 'bottom bottom', scrub: 1 } });
						gsap.to('.po-aurora--2', { yPercent: -24, ease: 'none', scrollTrigger: { trigger: root, start: 'top top', end: 'bottom bottom', scrub: 1.2 } });
						gsap.to('.po-aurora--3', { yPercent: 16, ease: 'none', scrollTrigger: { trigger: root, start: 'top top', end: 'bottom bottom', scrub: 1.4 } });

						// Plugin panel mock reveal (teased below the fold, revealed on scroll).
						if (root.querySelector('.po-intro')) {
							gsap.from('.po-intro .po-hero__panel', { autoAlpha: 0, y: 50, duration: 0.9, ease: 'power3.out', scrollTrigger: { trigger: '.po-intro', start: 'top 82%', toggleActions: 'play none none reverse' } });
						}

						// Set-apart tutorial promo reveal (only present for some plugins).
						if (root.querySelector('.po-tutorial__card')) {
							gsap.from('.po-tutorial__card', { autoAlpha: 0, y: 40, duration: 0.7, ease: 'power3.out', scrollTrigger: { trigger: '.po-tutorial', start: 'top 85%', toggleActions: 'play none none reverse' } });
						}

						gsap.utils.toArray<HTMLElement>('.po-step').forEach((step) => {
							const idx = Number(step.dataset.step);
							const panel = step.querySelector<HTMLElement>('.po-panel-anim');
							const copy = step.querySelectorAll('.po-reveal');
							const ghost = step.querySelector<HTMLElement>('.po-ghost');
							const callout = step.querySelector<HTMLElement>('.po-callout');
							const rings = Array.from(step.querySelectorAll<HTMLElement>('.po-ring'));

							if (ghost) {
								gsap.fromTo(ghost, { yPercent: 28 }, { yPercent: -28, ease: 'none', scrollTrigger: { trigger: step, start: 'top bottom', end: 'bottom top', scrub: 1 } });
							}

							const tl = gsap.timeline({ scrollTrigger: { trigger: step, start: 'top 70%', toggleActions: 'play none none reverse' } });
							if (panel) {
								gsap.set(panel, { transformPerspective: 1000 });
								tl.from(panel, { autoAlpha: 0, y: 60, rotateX: 8, duration: 0.9, ease: 'power3.out' });
							}
							tl.from(copy, { autoAlpha: 0, y: 22, stagger: 0.1, duration: 0.55, ease: 'power2.out' }, panel ? '-=0.55' : 0);
							if (callout) {
								tl.from(callout, { autoAlpha: 0, y: 10, scale: 0.9, duration: 0.4, ease: 'back.out(1.7)' }, '-=0.1');
							}

							const pulses = rings.map((r) => gsap.to(r, { scale: 1.12, duration: 1.2, repeat: -1, yoyo: true, ease: 'sine.inOut', paused: true }));
							const stepTl = this.buildDuoTimeline(step) || this.buildTypeCommandTimeline(step) || this.buildOpenPanelTimeline(step) || this.buildCogTimeline(step);

							ScrollTrigger.create({
								trigger: step,
								start: 'top 58%',
								end: 'bottom 42%',
								onToggle: (self) => {
									step.classList.toggle('has-spot', self.isActive);
									if (panel) {
										panel.classList.toggle('has-spot', self.isActive);
									}
									if (self.isActive) {
										setActive(idx);
										pulses.forEach((p) => p.play());
										if (stepTl) { stepTl.play(); }
									} else {
										pulses.forEach((p) => p.pause());
										if (stepTl) { stepTl.pause(0); }
									}
								},
							});
						});
					});

					// Reduced motion: no transforms, just reveal + rail tracking.
					mm.add('(prefers-reduced-motion: reduce)', () => {
						gsap.utils.toArray<HTMLElement>('.po-step').forEach((step) => {
							const idx = Number(step.dataset.step);
							const panel = step.querySelector<HTMLElement>('.po-panel-anim');
							ScrollTrigger.create({
								trigger: step, start: 'top 58%', end: 'bottom 42%',
								onToggle: (self) => {
									step.classList.toggle('has-spot', self.isActive);
									if (panel) { panel.classList.toggle('has-spot', self.isActive); }
									if (self.isActive) { setActive(idx); }
								},
							});
						});
					});
				}, root);

				window.addEventListener('load', this.refreshTriggers);
				// Layout can shift as fonts settle; refresh shortly after mount.
				window.setTimeout(this.refreshTriggers, 400);
			} catch (err) {
				// If anything in the animation setup throws, the page still
				// renders fully — content is visible by default in CSS.
				// eslint-disable-next-line no-console
				console.warn('[PluginOnboarding] animation init skipped:', err);
			}
		},
		refreshTriggers () {
			ScrollTrigger.refresh();
		},
		buildDuoTimeline (step: HTMLElement): gsap.core.Timeline | null {
			const duo = step.querySelector<HTMLElement>('.po-duo');
			if (!duo) { return null; }
			const kind = duo.dataset.duo;
			const tl = gsap.timeline({ repeat: -1, repeatDelay: 1.1, paused: true });

			// In-place edit: a scan sweeps the marquee and the selected region
			// re-renders (before -> after). The edit happens in place — no fly.
			if (kind === 'edit') {
				const after = duo.querySelector<HTMLElement>('.po-edit-after');
				const scan = duo.querySelector<HTMLElement>('.po-edit-scan');
				const badge = duo.querySelector<HTMLElement>('.po-edit-badge');
				if (!after) { return null; }
				tl.set(after, { autoAlpha: 0 })
					.set(scan, { autoAlpha: 0, yPercent: -120 })
					.set(badge, { autoAlpha: 0, y: 6, scale: 0.9 })
					.to(scan, { autoAlpha: 1, duration: 0.15 })
					.to(scan, { yPercent: 120, duration: 1.0, ease: 'sine.inOut' })
					.to(after, { autoAlpha: 1, duration: 0.6 }, '<0.15')
					.to(scan, { autoAlpha: 0, duration: 0.15 }, '-=0.15')
					.to(badge, { autoAlpha: 1, y: 0, scale: 1, duration: 0.35, ease: 'back.out(2)' })
					.to({}, { duration: 1.2 })
					.to([after, badge], { autoAlpha: 0, duration: 0.45 });
				return tl;
			}

			// Selection: a marquee is drawn over the image, growing to its size.
			if (kind === 'select') {
				const marquee = duo.querySelector<HTMLElement>('.po-marquee');
				if (!marquee) { return null; }
				tl.set(marquee, { width: '0%', height: '0%', autoAlpha: 0 })
					.to(marquee, { autoAlpha: 1, duration: 0.15 })
					.to(marquee, { width: '42%', height: '44%', duration: 0.85, ease: 'power2.out' })
					.to({}, { duration: 1.5 });
				return tl;
			}

			// Queue: the render queue fills with up to four jobs, one at a time.
			if (kind === 'queue') {
				const rows = duo.querySelectorAll<HTMLElement>('.po-qrow');
				const foot = duo.querySelector<HTMLElement>('.po-queue__foot');
				if (!rows.length) { return null; }
				tl.set(rows, { autoAlpha: 0, y: 12, scale: 0.96 });
				if (foot) { tl.set(foot, { autoAlpha: 0.35 }); }
				rows.forEach((row) => {
					tl.to(row, { autoAlpha: 1, y: 0, scale: 1, duration: 0.42, ease: 'back.out(1.5)' }, '+=0.32');
				});
				if (foot) { tl.to(foot, { autoAlpha: 1, duration: 0.4 }, '-=0.2'); }
				tl.to({}, { duration: 1.6 });
				return tl;
			}

			// Fly / drag: an element travels from the panel to a target on the surface.
			const fly = duo.querySelector<HTMLElement>('.po-fly');
			const target = duo.querySelector<HTMLElement>('.po-target');
			if (!fly || !target) { return null; }
			const f = fly.getBoundingClientRect();
			const t = target.getBoundingClientRect();
			const dx = (t.left + t.width / 2) - (f.left + f.width / 2);
			const dy = (t.top + t.height / 2) - (f.top + f.height / 2);
			const sparks = duo.querySelectorAll('.po-spark');
			const cursor = duo.querySelector<HTMLElement>('.po-cursor');

			if (kind === 'generate') {
				tl.set(fly, { x: 0, y: 0, scale: 0.5, autoAlpha: 0 })
					.set(sparks, { autoAlpha: 0, scale: 0 })
					.to(fly, { autoAlpha: 1, scale: 1, duration: 0.45, ease: 'back.out(2)' })
					.to(fly, { x: dx, y: dy, duration: 1.0, ease: 'power2.inOut' }, '+=0.25')
					.to(fly, { scale: 1.12, duration: 0.25, ease: 'power2.out' })
					.to(sparks, { autoAlpha: 1, scale: 1, stagger: 0.06, duration: 0.3 }, '<')
					.to(fly, { scale: 1.04, duration: 0.3 })
					.to([fly, sparks], { autoAlpha: 0, duration: 0.45 }, '+=0.7');
			} else if (kind === 'drag') {
				tl.set(fly, { x: 0, y: 0, scale: 1, autoAlpha: 1 })
					.set(cursor, { x: 0, y: 0, autoAlpha: 0 })
					.to(cursor, { autoAlpha: 1, duration: 0.3 })
					.to([fly, cursor], { scale: 1.08, duration: 0.25, ease: 'power2.out' })
					.to([fly, cursor], { x: dx, y: dy, duration: 1.1, ease: 'power2.inOut' }, '+=0.15')
					.to(fly, { scale: 1, duration: 0.3, ease: 'power2.out' })
					.to(cursor, { autoAlpha: 0, duration: 0.3 }, '<')
					.to(fly, { autoAlpha: 0, duration: 0.45, delay: 0.6 });
			}
			return tl;
		},
		buildTypeCommandTimeline (step: HTMLElement): gsap.core.Timeline | null {
			// Rhino opens the panel by typing at the command line, not by clicking a
			// ribbon button, so this types the command out a character at a time and
			// docks the panel on Enter. No TextPlugin: a proxy object is tweened and
			// its rounded value slices the word.
			const scene = step.querySelector<HTMLElement>('.po-rhscene');
			if (!scene) { return null; }
			const text = scene.querySelector<HTMLElement>('.po-rhcmd__text');
			const panel = scene.querySelector<HTMLElement>('.po-openpanel');
			const echo = scene.querySelector<HTMLElement>('.po-rhcmd__echo');
			if (!text || !panel) { return null; }

			const word = 'RunDiffusion';
			const typed = { n: 0 };
			const write = () => { text.textContent = word.slice(0, Math.round(typed.n)); };

			const tl = gsap.timeline({ repeat: -1, repeatDelay: 1.5, paused: true });
			tl.set(panel, { xPercent: 120, autoAlpha: 0 })
				.call(() => { typed.n = 0; write(); })
				.set(echo, { autoAlpha: 0 })
				.to({}, { duration: 0.45 })
				.to(typed, { n: word.length, duration: 1.05, ease: 'none', onUpdate: write })
				.to({}, { duration: 0.4 })
				.to(echo, { autoAlpha: 1, duration: 0.25 })
				.to(panel, { xPercent: 0, autoAlpha: 1, duration: 0.6, ease: 'power3.out' }, '-=0.1')
				.to({}, { duration: 1.5 });
			return tl;
		},
		buildOpenPanelTimeline (step: HTMLElement): gsap.core.Timeline | null {
			const scene = step.querySelector<HTMLElement>('.po-scene');
			if (!scene) { return null; }
			const panel = scene.querySelector<HTMLElement>('.po-openpanel');
			const cursor = scene.querySelector<HTMLElement>('.po-scene-cursor');
			const icon = scene.querySelector<HTMLElement>('.po-click-target');
			const ripple = scene.querySelector<HTMLElement>('.po-ripple');
			const toolbar = scene.querySelector<HTMLElement>('.po-mtoolbar');
			if (!panel || !cursor || !icon) { return null; }

			// Slide the panel in from whichever edge it docks to (left for the
			// Miro toolbar, right for the Photoshop dock).
			const fromRight = scene.dataset.dock === 'right';
			// Land the cursor tip on the click target (toolbar icon / menu item).
			const c = cursor.getBoundingClientRect();
			const ic = icon.getBoundingClientRect();
			const cdx = (ic.left + ic.width / 2) - (c.left + c.width * 0.25);
			const cdy = (ic.top + ic.height / 2) - (c.top + c.height * 0.2);

			const tl = gsap.timeline({ repeat: -1, repeatDelay: 1.3, paused: true });
			tl.set(panel, { xPercent: fromRight ? 120 : -120, autoAlpha: 0 })
				.set(cursor, { autoAlpha: 0, x: 0, y: 0, scale: 1 })
				.set(ripple, { autoAlpha: 0, scale: 0.5 });
			if (toolbar) { tl.set(toolbar, { x: 0, yPercent: -50 }); }
			tl.to(cursor, { autoAlpha: 1, duration: 0.3 })
				.to(cursor, { x: cdx, y: cdy, duration: 0.75, ease: 'power2.inOut' })
				.to(cursor, { scale: 0.82, duration: 0.12, ease: 'power2.out' })
				.to(icon, { scale: 0.9, duration: 0.12, ease: 'power2.out' }, '<')
				.to(ripple, { autoAlpha: 0.9, scale: 1.7, duration: 0.45, ease: 'power2.out' }, '<')
				.to(cursor, { scale: 1, duration: 0.16, ease: 'back.out(3)' })
				.to(icon, { scale: 1, duration: 0.18, ease: 'back.out(3)' }, '<')
				.set(ripple, { autoAlpha: 0 })
				.to(panel, { xPercent: 0, autoAlpha: 1, duration: 0.6, ease: 'power3.out' }, '+=0.05');
			if (toolbar) { tl.to(toolbar, { x: 16, duration: 0.6, ease: 'power3.out' }, '<'); }
			tl.to(cursor, { autoAlpha: 0, duration: 0.3 }, '<0.1')
				.to({}, { duration: 0.9 });
			return tl;
		},
		buildCogTimeline (step: HTMLElement): gsap.core.Timeline | null {
			// Photoshop account flow: click the cog -> Settings tab -> account picker.
			const flow = step.querySelector<HTMLElement>('.po-cogflow');
			if (!flow) { return null; }
			const cog = flow.querySelector<HTMLElement>('.po-click-target');
			const ripple = flow.querySelector<HTMLElement>('.po-ripple');
			const cursor = flow.querySelector<HTMLElement>('.po-scene-cursor');
			const view = flow.querySelector<HTMLElement>('.po-settings-view');
			const editTab = flow.querySelector<HTMLElement>('.po-tab--edit');
			const settingsTab = flow.querySelector<HTMLElement>('.po-tab--settings');
			if (!cog || !cursor || !view) { return null; }

			const c = cursor.getBoundingClientRect();
			const ic = cog.getBoundingClientRect();
			const cdx = (ic.left + ic.width / 2) - (c.left + c.width * 0.25);
			const cdy = (ic.top + ic.height / 2) - (c.top + c.height * 0.2);
			const setEdit = () => { if (editTab) { editTab.classList.add('is-on'); } if (settingsTab) { settingsTab.classList.remove('is-on'); } };
			const setSettings = () => { if (editTab) { editTab.classList.remove('is-on'); } if (settingsTab) { settingsTab.classList.add('is-on'); } };

			const tl = gsap.timeline({ repeat: -1, repeatDelay: 1.2, paused: true });
			tl.set(view, { autoAlpha: 0, y: 8 })
				.set(cursor, { autoAlpha: 0, x: 0, y: 0, scale: 1 })
				.set(ripple, { autoAlpha: 0, scale: 0.5 })
				.set(cog, { rotation: 0 })
				.add(setEdit)
				.to(cursor, { autoAlpha: 1, duration: 0.3 })
				.to(cursor, { x: cdx, y: cdy, duration: 0.7, ease: 'power2.inOut' })
				.to(cursor, { scale: 0.82, duration: 0.12, ease: 'power2.out' })
				.to(cog, { rotation: 90, duration: 0.45, ease: 'power2.out' }, '<')
				.to(ripple, { autoAlpha: 0.9, scale: 1.7, duration: 0.4, ease: 'power2.out' }, '<')
				.to(cursor, { scale: 1, duration: 0.15, ease: 'back.out(3)' })
				.set(ripple, { autoAlpha: 0 })
				.add(setSettings)
				.to(view, { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out' }, '+=0.05')
				.to(cursor, { autoAlpha: 0, duration: 0.3 }, '<0.1')
				.to({}, { duration: 1.4 });
			return tl;
		},
	},
});
